import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { postJSON } from 'api/fetch';
import { pollWorkerStatus } from 'api/workerStatus';
import { captureException } from 'fe-core/util/errorTracking';

import { navigateToSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/navigateToSchedule';
import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';
import { selectJobIdUserIdMap } from 'features/scheduleBuilder/ScheduleBuilderView/selectors/shared';
import { showNotification } from 'features/scheduleBuilder/ScheduleBuilderView/slice';
import { selectUserIdUserNameMap } from 'features/scheduleBuilder/slices/users/slice';

import { displayMonthAndDay } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import { captureHydrationException, selectSliceAttribute } from '../helpers';
import { sortJobPositions } from '../jobPositions/slice';
import { RootState } from '../scheduleBuilder';
import { repeatTemplate } from '../templates/slice';

import { reloadSchedule } from './actions';
import { SLICE_NAME } from './constants';
import type {
  CopySchedulePayload,
  CopyScheduleResponse,
  ResendNotificationsPayload,
  RevertSchedulePayload,
  ScheduleRelationships,
  ScheduleSerializedEntity,
  ScheduleState,
} from './types';

export const initialState: ScheduleState = {
  startDate: '',
  endDate: '',
  isJumpstartVisible: false,
  sortByWeek: 'first_name',
  sortByDay: 'first_name',
  isSmsNotifyScheduleEnabled: false,
  isScheduleInsightsModalDismissed: false,
  isScheduleInsightsTooltipDismissed: false,
  smsDiscoverability: false,
  deletedShiftsCount: 0,
  isAddShiftNotesReminderEnabled: false,
  status: 'idle',
  error: undefined,
  shifts: [],
  events: [],
  jobs: [],
  roles: [],
  jobPositions: [],
  customJobPositions: [],
  timeOffs: [],
  eventQuotas: [],
  shiftNotesQuotas: [],
  timeOffBlackoutPeriods: [],
  scheduleAvailabilities: [],
  shiftLaborTotals: [],
  locations: [],
  claimRequests: [],
  shiftNotes: [],
  shiftEvents: [],
};

export const copySchedule = createAsyncThunk(
  `${SLICE_NAME}/schedule/copySchedule`,
  async (
    {
      start_date = '',
      end_date = '',
      target_start_date = '',
      target_end_date = '',
      department_ids = [],
      role_ids = [],
      job_ids = [],
      includes_shifts_without_roles,
      noSuccessBanner,
      noRedirect,
    }: CopySchedulePayload,
    { dispatch, getState }
  ) => {
    try {
      const state = getState();

      const response =
        (await postJSON('/schedule_builder/schedule/copy', {
          start_date,
          end_date,
          target_start_date,
          target_end_date,
          department_ids,
          role_ids,
          job_ids,
          ...(includes_shifts_without_roles && {
            includes_shifts_without_roles,
          }),
          ...(noSuccessBanner && { noSuccessBanner }),
          ...(noRedirect && { noRedirect }),
        })) || {};

      const jobId = response?.data?.id;
      if (jobId) {
        return pollWorkerStatus(jobId).then(
          (response: CopyScheduleResponse) => {
            if (!noSuccessBanner) {
              dispatch(
                showNotification({
                  message: toI18n(
                    'schedule_builder.react_page.copy_schedule_dialog.success_message',
                    {
                      props: {
                        start_date: displayMonthAndDay(start_date),
                        end_date: displayMonthAndDay(end_date),
                        target_start_date:
                          displayMonthAndDay(target_start_date),
                        target_end_date: displayMonthAndDay(target_end_date),
                      },
                    }
                  ),
                  type: 'success',
                })
              );
            }

            if (!noRedirect) {
              navigateToSchedule(state, {
                rangeStart: target_start_date,
                rangeEnd: target_end_date,
              });
            }

            return response;
          }
        );
      }

      return response;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const repeatSchedule = createAsyncThunk(
  `${SLICE_NAME}/repeatSchedule`,
  (
    {
      weekStart,
      weekEnd,
      numberOfWeeks,
    }: { weekStart: string; weekEnd: string; numberOfWeeks: number },
    { dispatch }
  ) =>
    dispatch(
      repeatTemplate({
        start_date: weekStart,
        end_date: weekEnd,
        repeat_weeks: numberOfWeeks,
      })
    ).then(() => {
      showNotification({
        message: toI18n(
          `schedule_builder.publish_drawer.repeat_schedule.success_banner.${
            numberOfWeeks === 1 ? 'one' : 'other'
          }`,
          {
            props: {
              count: numberOfWeeks,
              week: `${displayMonthAndDay(weekStart)} - ${displayMonthAndDay(
                weekEnd
              )}`,
            },
          }
        ),
      });
    })
);

export const publishSchedule = createAsyncThunk(
  `${SLICE_NAME}/publish`,
  async ({
    beforeDigestCallback,
    payload,
  }: {
    beforeDigestCallback?: () => void;
    payload: { startDate: string; endDate: string };
  }) => {
    try {
      const response = await postJSON(
        '/schedule_builder/schedule/publish',
        payload
      );
      if (beforeDigestCallback) {
        beforeDigestCallback();
      }
      return response;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const resendNotifications = createAsyncThunk(
  `${SLICE_NAME}/resendNotifications`,
  async (payload: ResendNotificationsPayload) => {
    try {
      const response = await postJSON(
        '/schedule_builder/schedule/resend_notifications',
        payload
      );
      return response;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const clearSchedule = createAsyncThunk(
  `${SLICE_NAME}/clearSchedule`,
  async (payload: any, { dispatch }) => {
    try {
      const response = await postJSON(
        '/schedule_builder/schedule/clear',
        payload
      );
      const jobId = response?.data?.id;
      if (jobId) {
        return pollWorkerStatus(jobId).then(() => {
          dispatch(
            reloadSchedule({
              startDate: payload.start_date,
              endDate: payload.end_date,
              force: true,
            })
          );
        });
      }

      return response;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const clearEntireSchedule = createAsyncThunk(
  'scheduleBuilder/clearEntireSchedule',
  (payload, { dispatch }) => dispatch(clearSchedule(payload))
);

export const scheduleAttributes = function (
  schedule: ScheduleSerializedEntity
) {
  return {
    id: schedule.id,
    startDate: schedule.attributes.startDate,
    endDate: schedule.attributes.endDate,
    isJumpstartVisible: schedule.attributes.isJumpstartVisible,
    isScheduleInsightsModalDismissed:
      schedule.attributes.isScheduleInsightsModalDismissed,
    isScheduleInsightsTooltipDismissed:
      schedule.attributes.isScheduleInsightsTooltipDismissed,
    sortByWeek: schedule.attributes.sortByWeek,
    isSmsNotifyScheduleEnabled: schedule.attributes.isSmsNotifyScheduleEnabled,
    smsDiscoverability: schedule.attributes.smsDiscoverability,
    deletedShiftsCount: schedule.attributes.deletedShiftsCount,
    isAddShiftNotesReminderEnabled:
      schedule.attributes.isAddShiftNotesReminderEnabled,
  };
};

export const revertSchedule = createAsyncThunk(
  `${SLICE_NAME}/revert`,
  async (payload: RevertSchedulePayload, { dispatch }) => {
    try {
      const response = await postJSON(
        '/schedule_builder/schedule/revert',
        payload
      );
      const jobId = response?.data?.id;
      if (jobId) {
        return pollWorkerStatus(jobId).then(() => {
          dispatch(
            reloadSchedule({
              startDate: payload.start_date,
              endDate: payload.end_date,
              force: true,
            })
          );
        });
      }
      return response;
    } catch (error) {
      captureException(error);
    }
  }
);

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    incrementDeletedShiftsCount: state => {
      state.deletedShiftsCount += 1;
    },
    setSortByWeek: (state, action) => {
      state.sortByWeek = action.payload;
    },
    setSortByDay: (state, action) => {
      state.sortByDay = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchSchedule.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchSchedule.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to get schedule';
    });
    builder.addCase(fetchSchedule.fulfilled, (state, action) => {
      if (action.payload?.data) {
        const schedule: ScheduleSerializedEntity = action?.payload?.data;
        try {
          const attributes = scheduleAttributes(schedule);
          const relationships: ScheduleRelationships = {};

          for (const [k, v] of Object.entries(schedule.relationships || {})) {
            relationships[k] = v.data;
          }

          Object.assign(state, attributes, relationships);
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
      state.status = 'succeeded';
    });

    builder.addCase(publishSchedule.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(publishSchedule.fulfilled, (state, action) => {
      if (action.payload?.data) {
        const schedule: ScheduleSerializedEntity = action?.payload?.data;
        try {
          const attributes = scheduleAttributes(schedule);
          const relationships: ScheduleRelationships = {};

          for (const [k, v] of Object.entries(schedule.relationships || {})) {
            relationships[k] = v.data;
          }

          Object.assign(state, attributes, relationships);

          state.status = 'succeeded';
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    });
    builder.addCase(publishSchedule.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to publish schedule';
    });

    // Handle resendNotifications thunk states
    builder.addCase(resendNotifications.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(resendNotifications.fulfilled, state => {
      state.status = 'succeeded';
    });
    builder.addCase(resendNotifications.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to resend notifications';
    });
    builder.addCase(copySchedule.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(copySchedule.fulfilled, state => {
      state.status = 'succeeded';
    });
    builder.addCase(copySchedule.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to copy schedule';
    });

    builder.addCase(revertSchedule.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(revertSchedule.fulfilled, state => {
      state.status = 'succeeded';
    });
    builder.addCase(revertSchedule.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to revert schedule';
    });

    builder.addCase(clearSchedule.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(clearSchedule.fulfilled, state => {
      state.status = 'succeeded';
    });
    builder.addCase(clearSchedule.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to clear schedule';
    });
  },
});

export const updateSortByWeek =
  (payload: { sortByWeek: string; ids: any }) => (dispatch: any) =>
    // Still need to call JobPosition.sort for side effects
    dispatch(
      sortJobPositions({
        sort_by: payload.sortByWeek,
        ids: payload.ids,
      })
    ).then(() => {
      dispatch(slice.actions.setSortByWeek(payload.sortByWeek));
    });

export const updateSortByDay =
  (payload: { sortByDay: any }) => (dispatch: any) => {
    dispatch(slice.actions.setSortByDay(payload.sortByDay));
  };

export const selectSortByWeek = (state: any) =>
  selectSliceAttribute({
    state,
    attribute: 'sortByWeek',
    sliceName: 'schedule',
  });

export const selectIsScheduleInsightsTooltipDismissed = (state: any) =>
  selectSliceAttribute({
    state,
    attribute: 'isScheduleInsightsTooltipDismissed',
    sliceName: 'schedule',
  });

export const selectIsScheduleInsightsModalDismissed = (state: any) =>
  selectSliceAttribute({
    state,
    attribute: 'isScheduleInsightsModalDismissed',
    sliceName: 'schedule',
  });

export const scheduleFetchIsPending = (state: RootState) =>
  selectSliceAttribute({
    state,
    attribute: 'status',
    sliceName: 'schedule',
  }) === 'pending';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const selectTeamMemberOptions = createSelector(
  selectJobIdUserIdMap,
  selectUserIdUserNameMap,
  (
    jobIdUserIdMap: Record<string, string>,
    userIdUserNameMap: Record<
      string,
      { fullName: string; firstName: string; lastName: string }
    >
  ) =>
    Object.entries(jobIdUserIdMap)
      .map(([jobId, userId]) => ({
        label: userIdUserNameMap[userId]?.fullName,
        value: parseInt(jobId, 10),
      }))
      .filter((option): option is { label: string; value: number } =>
        Boolean(option.label)
      )
      .sort((a, b) => a.label.localeCompare(b.label))
);

export const { setSortByDay, setSortByWeek, incrementDeletedShiftsCount } =
  slice.actions;

export const { reducer } = slice;
